import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { PageHeader, StatusBadge, SeverityBadge, fmtDate } from "@/components/shared";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AlertTriangle, Activity } from "lucide-react";

const HEALTH_DOT = { healthy: "bg-green-500", warning: "bg-amber-500", critical: "bg-red-500" };
const SEV_STYLE = {
  critical: "border-red-200 bg-red-50",
  warning: "border-amber-200 bg-amber-50",
  info: "border-border bg-muted/40",
};

export default function NetworkHealth() {
  const navigate = useNavigate();
  const [locs, setLocs] = useState([]);
  const [attention, setAttention] = useState([]);

  useEffect(() => {
    api.get("/network/locations").then((r) => setLocs(r.data)).catch(() => {});
    api.get("/network/attention").then((r) => setAttention(r.data)).catch(() => {});
  }, []);

  return (
    <div>
      <PageHeader title="Network Health" subtitle="Full operational view of every franchise location." />

      <Card className="border-border overflow-hidden mb-6">
        <CardHeader><CardTitle className="font-heading text-lg flex items-center gap-2"><Activity size={18} strokeWidth={1.5} /> Location Health</CardTitle></CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          <Table data-testid="nh-location-table">
            <TableHeader>
              <TableRow className="bg-muted/40 hover:bg-muted/40">
                {["Location", "City", "Country", "Status", "Owner", "Trainers", "Clients", "Dogs", "Upcoming", "Attend.", "HW", "Quality", "Last activity", "Health"].map((h) => (
                  <TableHead key={h} className="text-xs uppercase tracking-wide font-semibold whitespace-nowrap">{h}</TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {locs.map((l) => (
                <TableRow key={l.id} className="hover:bg-muted/40 cursor-pointer" onClick={() => navigate(`/locations/${l.id}`)} data-testid="nh-location-row">
                  <TableCell className="font-medium whitespace-nowrap">{l.name}</TableCell>
                  <TableCell>{l.city}</TableCell>
                  <TableCell>{l.country}</TableCell>
                  <TableCell><StatusBadge value={l.status} /></TableCell>
                  <TableCell className="whitespace-nowrap">{l.owner_name}</TableCell>
                  <TableCell>{l.active_trainers}</TableCell>
                  <TableCell>{l.active_clients}</TableCell>
                  <TableCell>{l.active_dogs}</TableCell>
                  <TableCell>{l.upcoming_classes}</TableCell>
                  <TableCell>{l.attendance_rate}%</TableCell>
                  <TableCell>{l.homework_completion_rate}%</TableCell>
                  <TableCell className="font-medium">{l.quality_score}</TableCell>
                  <TableCell className="whitespace-nowrap">{fmtDate(l.last_activity)}</TableCell>
                  <TableCell>
                    <span className="flex items-center gap-1.5 text-xs capitalize whitespace-nowrap">
                      <span className={`h-2 w-2 rounded-full ${HEALTH_DOT[l.attention]}`} /> {l.attention}
                    </span>
                  </TableCell>
                </TableRow>
              ))}
              {locs.length === 0 && <TableRow><TableCell colSpan={14} className="text-center py-10 text-muted-foreground">No locations.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card className="border-border">
        <CardHeader><CardTitle className="font-heading text-lg flex items-center gap-2"><AlertTriangle size={18} strokeWidth={1.5} className="text-amber-600" /> Attention Center</CardTitle></CardHeader>
        <CardContent>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {attention.length ? attention.map((a, i) => (
              <div key={i} className={`p-3 rounded-lg border ${SEV_STYLE[a.severity]}`} data-testid="nh-attention-item">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] uppercase tracking-wide font-semibold text-muted-foreground">{a.category}</span>
                  <SeverityBadge value={a.severity} />
                </div>
                <div className="text-sm font-medium mt-1">{a.title}</div>
                {a.detail && <div className="text-xs text-muted-foreground mt-0.5">{a.detail}</div>}
              </div>
            )) : <p className="text-sm text-muted-foreground py-6 text-center col-span-full">Network is healthy.</p>}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
